import { type PropsWithChildren } from '@shared/utils';
import { createContext, useCallback, useMemo, useState } from 'react';

import { ItemSetQuestionChangeModal, SingleQuestionChangeModal } from '../../containers';
import { QuestionChangeInformationProvider } from '../question-change-information-provider';

export enum QuestionChangeModalAction {
  ITEM_SET_QUESTION_CHANGE = 'ITEM_SET_QUESTION_CHANGE',
  SINGLE_QUESTION_CHANGE = 'SINGLE_QUESTION_CHANGE',
}

interface QuestionChangeContextData {
  action: QuestionChangeModalAction | null;
  setAction: (action: QuestionChangeModalAction) => void;
}

interface QuestionChangeModalProviderProps extends PropsWithChildren {
  questionId: string;
  questionGroupSize?: number | null;
  qSetQuestionId: string;
  onComplete?: () => void;
}
export const QuestionChangeModalContext = createContext<QuestionChangeContextData | undefined>(
  undefined,
);

export default function QuestionChangeModalProvider(
  props: Readonly<QuestionChangeModalProviderProps>,
) {
  const { children, questionId, qSetQuestionId, onComplete, questionGroupSize } = props;
  const [action, setAction] = useState<QuestionChangeModalAction | null>(null);

  const handleModalClose = useCallback(() => {
    setAction(null);
  }, [setAction]);

  const handleSetAction = useCallback(
    (action: QuestionChangeModalAction | null) => {
      setAction(action);
    },
    [setAction],
  );

  const isOpenModal = useMemo(() => action !== null, [action]);

  const contextValue = useMemo(
    () => ({
      action,
      setAction: handleSetAction,
    }),
    [action, handleSetAction, handleModalClose],
  );

  return (
    <QuestionChangeModalContext.Provider value={contextValue}>
      {children}
      {isOpenModal ? (
        <QuestionChangeInformationProvider
          initialValues={questionGroupSize ? { questionGroupSize } : undefined}
        >
          <SingleQuestionChangeModal
            questionId={questionId}
            qSetQuestionId={qSetQuestionId}
            open={action === QuestionChangeModalAction.SINGLE_QUESTION_CHANGE}
            onClose={handleModalClose}
            onComplete={onComplete}
          />
          <ItemSetQuestionChangeModal
            questionId={questionId}
            qSetQuestionId={qSetQuestionId}
            open={action === QuestionChangeModalAction.ITEM_SET_QUESTION_CHANGE}
            onClose={handleModalClose}
            onComplete={onComplete}
          />
        </QuestionChangeInformationProvider>
      ) : null}
    </QuestionChangeModalContext.Provider>
  );
}
